// src/components/Admin/Campaigns/builder/ai/GuidePanel.tsx
//
// The grounded conversational Guide for the Campaign Builder admin — a fixed
// in-panel column (not a floating launcher) meant to be mounted in the builder's
// 3-pane shell.
//
// Why useChatStream and NOT useAiChat:
//   useAiChat owns its own `input` and derives BOTH the wire payload and the
//   displayed user bubble from that same string (sendMessage sends `input`
//   verbatim and echoes it via onUserMessage). We need to send a grounding
//   preamble + the question on the wire while showing ONLY the admin's typed
//   text in the bubble, and we need the raw assistant text to run parseReply on.
//   useChatStream.send({ payload, onAssistantToken, onAssistantMessage, ... })
//   gives us full control of payload.message and our own message state, which is
//   the cleaner fit. We keep our own session_id, messages array, and a ref of the
//   last-sent grounding so the (large) preamble is only re-sent when the campaign
//   / selected-step grounding actually changes.
//
// Safety: nothing is ever applied automatically. Every proposed edit / step set
// is delegated to the parent via onApplyEdit / onAddSteps. parseReply already
// guards malformed JSON; we additionally wrap it so a bad reply can never throw
// during render.

import React from 'react';
import { useChatStream } from '../../../../../hooks/useChatStream';
import {
  Campaign,
  CampaignStep,
  stepKindLabel,
  trackLabel,
} from '../../../../../support/campaigns';
import { StepStub } from '../../ai/campaignFlows';
import {
  ALLOWED_CAMPAIGN_FIELDS,
  ALLOWED_STEP_FIELDS,
  AssistDirective,
  buildGrounding,
  parseReply,
} from '../../ai/campaignAssist';
import { GhostButton, PrimaryButton, SectionHeader } from '../primitives';
import StbMarkdown from '../../../../security-toolbox-ui/StbMarkdown';
import styles from './guide.module.css';

type UiMessage = {
  role: 'user' | 'assistant';
  content: string;
  streaming?: boolean;
};

const QUICK_PROMPTS = [
  'Suggest steps for this campaign',
  "Improve the selected step's description",
  "What's missing from this campaign?",
];

function newSessionId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `campaign-guide-${crypto.randomUUID()}`;
  }
  return `campaign-guide-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function GuidePanel(props: {
  campaign: Campaign;
  steps: CampaignStep[];
  selectedStep: CampaignStep | null;
  onApplyEdit: (
    target: 'campaign' | string,
    field: string,
    value: string | number | boolean
  ) => void;
  onAddSteps: (stubs: StepStub[]) => void;
  /** When set, the panel renders a close control in its header (used when it's docked as a drawer). */
  onClose?: () => void;
}): JSX.Element {
  const { campaign, steps, selectedStep, onApplyEdit, onAddSteps, onClose } = props;

  const { send, cancel, sending, streamStatus } = useChatStream();

  const [messages, setMessages] = React.useState<UiMessage[]>([]);
  const [input, setInput] = React.useState('');

  const sessionIdRef = React.useRef<string>(newSessionId());
  // The grounding preamble last put on the wire; only re-sent when it changes.
  const lastGroundingRef = React.useRef<string | null>(null);
  const streamRef = React.useRef<HTMLDivElement | null>(null);

  const selectedStepId = selectedStep?.id ?? null;

  const safeGrounding = React.useCallback((): string => {
    try {
      return buildGrounding({ campaign, steps, selectedStep });
    } catch {
      return '';
    }
  }, [campaign, steps, selectedStep]);

  React.useEffect(() => {
    const el = streamRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const appendAssistantToken = React.useCallback((token: string) => {
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.role === 'assistant' && last.streaming) {
        return [
          ...prev.slice(0, -1),
          { ...last, content: `${last.content}${token}` },
        ];
      }
      return [...prev, { role: 'assistant', content: token, streaming: true }];
    });
  }, []);

  const finalizeAssistant = React.useCallback((content: string) => {
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      // Replace the current turn's assistant bubble (streaming or an earlier
      // `message` event) rather than appending a duplicate.
      if (last && last.role === 'assistant') {
        return [...prev.slice(0, -1), { role: 'assistant', content }];
      }
      return [...prev, { role: 'assistant', content }];
    });
  }, []);

  const submit = React.useCallback(
    async (raw: string) => {
      const question = raw.trim();
      if (!question || sending) return;

      const grounding = safeGrounding();
      const changed = grounding !== '' && grounding !== lastGroundingRef.current;
      const wireMessage = changed
        ? `${grounding}\n\n---\n\nAdmin question:\n${question}`
        : question;
      lastGroundingRef.current = grounding;

      setInput('');
      // Show ONLY the typed text as the user bubble.
      setMessages((prev) => [...prev, { role: 'user', content: question }]);

      try {
        await send({
          payload: {
            session_id: sessionIdRef.current,
            message: wireMessage,
            retrieval: { drift: false },
            mcp: {
              enabled: false,
              server_ids: [],
              capability_names: [],
              force_refresh: false,
            },
            related_type: 'campaign',
            related_id: String(campaign.id),
          },
          onAssistantToken: appendAssistantToken,
          onAssistantMessage: (message) => finalizeAssistant(message.content),
        });
      } catch {
        finalizeAssistant(
          'Sorry — I could not reach the assistant just now. Please try again.'
        );
      }
    },
    [
      appendAssistantToken,
      campaign.id,
      finalizeAssistant,
      safeGrounding,
      send,
      sending,
    ]
  );

  const onComposerKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit(input);
    }
  };

  const targetLabel = (target: string): string => {
    if (target === 'campaign') return 'campaign';
    if (target.startsWith('step:')) {
      const id = Number(target.slice('step:'.length));
      if (selectedStep && selectedStep.id === id) return `step "${selectedStep.name}"`;
      return 'step';
    }
    return target;
  };

  const renderDirective = (directive: AssistDirective) => (
    <div className={styles.proposal}>
      <p className={styles.proposalLabel}>Proposed changes</p>

      {directive.edits?.map((edit, i) => (
        <div className={styles.proposalRow} key={`edit-${i}`}>
          <span className={styles.proposalDesc}>
            <span className={styles.proposalField}>{edit.field}</span> on{' '}
            {targetLabel(edit.target)} →{' '}
            <span className={styles.proposalValue}>{String(edit.value)}</span>
          </span>
          <PrimaryButton
            className={styles.proposalBtn}
            onClick={() => onApplyEdit(edit.target, edit.field, edit.value)}
          >
            Apply
          </PrimaryButton>
        </div>
      ))}

      {directive.newSteps && directive.newSteps.length > 0 && (
        <div className={styles.proposalRow}>
          <span className={styles.proposalDesc}>
            {directive.newSteps.length} new step
            {directive.newSteps.length === 1 ? '' : 's'}:{' '}
            {directive.newSteps
              .map(
                (s) =>
                  s.name +
                  (s.track || s.step_kind
                    ? ` (${[s.track && trackLabel(s.track), s.step_kind && stepKindLabel(s.step_kind)]
                        .filter(Boolean)
                        .join(', ')})`
                    : '')
              )
              .join(', ')}
          </span>
          <PrimaryButton
            className={styles.proposalBtn}
            onClick={() => onAddSteps(directive.newSteps!)}
          >
            Add these steps
          </PrimaryButton>
        </div>
      )}
    </div>
  );

  const renderAssistant = (content: string) => {
    let text = content;
    let directive: AssistDirective | null = null;
    try {
      const parsed = parseReply(content, {
        campaignFields: ALLOWED_CAMPAIGN_FIELDS,
        stepFields: ALLOWED_STEP_FIELDS,
        selectedStepId,
      });
      text = parsed.text;
      directive = parsed.directive;
    } catch {
      // Defensive: never let a malformed reply throw during render.
      text = content;
      directive = null;
    }

    const hasDirective =
      !!directive &&
      ((directive.edits && directive.edits.length > 0) ||
        (directive.newSteps && directive.newSteps.length > 0));

    return (
      <>
        <StbMarkdown>{text || ''}</StbMarkdown>
        {hasDirective && renderDirective(directive as AssistDirective)}
      </>
    );
  };

  return (
    <div className={styles.guidePanel}>
      <div className={styles.guideHead}>
        <SectionHeader
          compact
          title="AI Guide"
          subtitle="Grounded on this campaign. Every suggestion is yours to apply."
          actions={
            onClose && (
              <GhostButton onClick={onClose} aria-label="Close the Guide">
                ✕
              </GhostButton>
            )
          }
        />
      </div>

      <div className={styles.guideStream} ref={streamRef}>
        {messages.length === 0 ? (
          <p className={styles.guideEmpty}>
            Ask about this campaign — I can suggest steps, sharpen descriptions,
            and point out gaps. I only propose changes; you apply them.
          </p>
        ) : (
          messages.map((m, i) => (
            <div
              key={i}
              className={`${styles.row} ${
                m.role === 'user' ? styles.rowUser : styles.rowBot
              }`}
            >
              <div
                className={`${styles.bubble} ${
                  m.role === 'user' ? styles.bubbleUser : styles.bubbleBot
                }`}
              >
                {m.role === 'user' ? (
                  m.content
                ) : (
                  <>
                    {renderAssistant(m.content)}
                    {m.streaming && <span className={styles.caret}>▍</span>}
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div className={styles.quickChips}>
        {QUICK_PROMPTS.map((prompt) => (
          <button
            key={prompt}
            type="button"
            className={styles.quickChip}
            onClick={() => submit(prompt)}
            disabled={sending}
          >
            {prompt}
          </button>
        ))}
      </div>

      {streamStatus && <div className={styles.guideStatus}>{streamStatus}</div>}

      <div className={styles.composer}>
        <textarea
          className={styles.composerInput}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onComposerKeyDown}
          placeholder="Ask the Guide…"
          rows={1}
          aria-label="Message the AI Guide"
        />
        {sending ? (
          <GhostButton className={styles.composerBtn} onClick={cancel}>
            Stop
          </GhostButton>
        ) : (
          <PrimaryButton
            className={styles.composerBtn}
            onClick={() => submit(input)}
            disabled={!input.trim()}
          >
            Send
          </PrimaryButton>
        )}
      </div>
    </div>
  );
}

export default GuidePanel;
